import {
    CreatePortfolioRequest,
    PortfolioResult,
    PortfolioService,
  } from '@/api/';
  import { action, atom, onSet } from 'nanostores';
  
  /**
   * The current selected portfolio
   */
  export const $portfolio = atom<PortfolioResult | undefined>();
  
  /**
   * The portfolios for the current selected user and org.
   */
  export const $portfolios = atom<PortfolioResult[]>([]);
  
  /**
   * Main function to load workspacs
   */
  const loadPortfolios = async (orgId: string) => {
    if (orgId) {
      console.log('Retrieving portfolios...');
  
      await ensureOrganization(orgId);
  
      var portfolios = await PortfolioService.getPortfolios(orgId);
  
      $portfolios.set(portfolios);
  
      // set the portfolio ids in local storage since we are gonna use it later
      // for drag and drop, these portfolio ids will act as order of the portfolios
      const existingPortfolioOrders = JSON.parse(
        localStorage.getItem('portfolios_orders') ?? '{}',
      );
      existingPortfolioOrders[orgId] = portfolios.map(w => w.portfolio.id);
      localStorage.setItem(
        'portfolios_orders',
        JSON.stringify(existingPortfolioOrders),
      );
    }
  };
  
  /**
   * Reload portfolios on org change
   */
  $org.subscribe(async org => {
    $portfolios.set([]);
  
    await loadPortfolios(org?.id ?? '');
  });
  
  /**
   * When the portfolio changes, we subscribe for notifications.
   */
  onSet($portfolio, async ws => {
    if (!ws.newValue) {
      return;
    }
  
    if (ws.newValue.portfolio.id == $portfolio.get()?.portfolio.id) {
      return;
    }
  
    await signalRService.joinPortfolioGroup(ws.newValue.portfolio.id);
  });
  
  /**
   * Watch for route changes; if we haven't loaded the orgs, we try to load them.
   */
  route.subscribe(async name => {
    if (name !== 'portfolios' || ($portfolios.value?.length ?? 0) > 0) {
      return;
    }
  
    const params = $router.get()?.params as Record<'organizationId', string>;
  
    await loadPortfolios(params.organizationId);
  });
  
  /**
   * Create a new portfolio
   * TODO: Include profile selection/invites.
   */
  export const createPortfolio = action(
    $portfolio,
    'createPortfolio',
    async (store, name: string, description?: string) => {
      const params = $router.get()?.params as Record<'organizationId', string>;
  
      const request: CreatePortfolioRequest = {
        name,
        description,
        orgId: params.organizationId,
      };
  
      const portfolio = await PortfolioService.createPortfolio(request);
  
      const result: PortfolioResult = {
        portfolio,
        invites: 0,
        collaborators: [],
        accountReceivablesCount: 0,
      };
  
      $portfolios.set([result, ...$portfolios.get()]);
  
      $portfolio.set(result);
    },
  );
  
  /**
   * Delete's the selected portfolio.
   */
  export const deletePortfolio = action(
    $portfolio,
    'deletePortfolio',
    async (store, portfolioId: string) => {
      await PortfolioService.deletePortfolio(portfolioId);
  
      $portfolio.set(undefined);
  
      // Splice out the project
      const portfolios = $portfolios.get();
  
      const index = portfolios.findIndex(w => w.portfolio.id === portfolioId);
  
      if (index > -1) {
        portfolios.splice(index, 1);
  
        $portfolios.set(portfolios);
      }
    },
  );
  
  /**
   * Ensure that the portfolio is loaded and selected.
   */
  export const ensurePortfolio = action(
    $portfolio,
    'ensurePortfolio',
    async (store, portfolioId: string) => {
      let pf: PortfolioResult | undefined;
  
      const params = $router.get()?.params as Record<'organizationId', string>;
  
      if ($portfolios.get().length === 0) {
        const portfolios = await PortfolioService.getPortfolios(
          params.organizationId ?? '',
        );
  
        $portfolios.set(portfolios);
  
        pf = portfolios.find(p => p.portfolio.id === portfolioId);
      } else {
        pf = $portfolios.get().find(p => p.portfolio.id === portfolioId);
      }
  
      $portfolio.set(pf);
    },
  );
